import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEffectOnce, useLocalStorage, useUpdateEffect } from 'react-use';
import AppReviewTable from 'talent_matching/components/table/app_review_table';
import BackgroundRankingBanner from 'talent_matching/components/table/background_ranking_banner';
import NoOverflowFullWidth from 'talent_matching/components/shared/no_overflow_full_width';
import { Row } from 'talent_matching/types';
import { usePeople } from 'talent_matching/hooks/use_people';
import { flashError } from 'flash/flash_manager';
import { getErrorTextFromResponse } from 'shared/utils/request';
import { useTalentMatchingTabContext } from 'talent_matching/contexts/talent_matching_tab_context';
import { TABS } from 'talent_matching/contexts/user_context';
import useSelectionsHelper from 'talent_matching/hooks/use_selections_helper';
import { useRankingInProgress } from 'talent_matching/contexts/ranking_in_progress_context';
import { useIndexingStatus } from 'talent_matching/contexts/indexing_status_context';
import {
  GridColumnVisibilityModel,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import useFilterTableState from 'talent_matching/hooks/use_filter_table_state';
import {
  ACTIONS_COLUMN_FIELD,
  useColumnVisibility,
} from 'talent_matching/hooks/use_column_visibility';
import { OPENSEARCH_MAX_RESULT_WINDOW } from 'talent_matching/opensearch_constraints';
import usePrefetchBuffer from 'talent_matching/hooks/use_prefetch_buffer';

const DEFAULT_COLUMN_VISIBILITY_MODEL: GridColumnVisibilityModel = {
  draggableHandle: false,
  resumeDetails: true,
  match: true,
  location: false,
  currentFeedback: false,
  schools: false,
  degree: false,
  discipline: false,
  tags: false,
  jobPost: false,
  fullName: true,
  [ACTIONS_COLUMN_FIELD]: true,
};

const AppReviewTableWrapper = () => {
  const {
    data: tableData,
    refetch: refetchPeople,
    isLoading,
    isFetching,
    isPlaceholderData,
    isError,
    error,
    queryKey,
  } = usePeople();

  useEffect(() => {
    if (isError) {
      flashError(getErrorTextFromResponse(error));
    }
  }, [isError, error]);

  usePrefetchBuffer({
    enabled: !!tableData && !isFetching,
    currentQueryKey: queryKey,
  });
  const unmatchedFilterValues = tableData?.unmatchedFilterValues;
  const rows = tableData?.rows || [];
  const buffer = tableData?.buffer || [];
  const totalRows = tableData?.totalRows || 0;
  // Pager can only reach rows within OpenSearch's max_result_window (GREEN-67215),
  // but the selection header and bulk-select UX should still reflect the true
  // match count.
  const pagerTotalRows = Math.min(totalRows, OPENSEARCH_MAX_RESULT_WINDOW);

  const {
    mountKey,
    currentPage,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    showLoader,
    triggerLoader,
  } = useFilterTableState({
    tabId: TABS.APP_REVIEW,
    totalRows: pagerTotalRows,
    isFetching,
  });

  const [preSelectedRow, setPreSelectedRow] = useState<Row | null>(null);

  const selectionsHelper = useSelectionsHelper(rows, totalRows);
  const { setPeopleFromSelections, removeSelections } = selectionsHelper;

  const onRowSelected = (selectedRows: GridRowSelectionModel) => {
    setPeopleFromSelections(selectedRows);
  };

  const { selectedStageId, canViewTalentMatchingAiMatchScore } =
    useTalentMatchingTabContext();
  // Selections belong to the stage they were made in.
  useEffect(() => {
    removeSelections();
  }, [mountKey, currentPage, rowsPerPage, selectedStageId, removeSelections]);

  const { rankingInProgress, isBackgroundRanking, dismissRankingCompleted } =
    useRankingInProgress();
  const { indexingInProgress } = useIndexingStatus();

  const prevRankingInProgress = useRef(rankingInProgress);
  useUpdateEffect(() => {
    const wasInProgress = prevRankingInProgress.current;
    prevRankingInProgress.current = rankingInProgress;

    if (wasInProgress && !rankingInProgress && !isBackgroundRanking) {
      refetchPeople();
    }
  }, [rankingInProgress, isBackgroundRanking, refetchPeople]);

  useUpdateEffect(() => {
    if (!indexingInProgress) {
      refetchPeople();
    }
  }, [indexingInProgress, refetchPeople]);

  const onRefreshAfterRanking = useCallback(() => {
    dismissRankingCompleted();
    triggerLoader();
    refetchPeople();
  }, [dismissRankingCompleted, triggerLoader, refetchPeople]);

  const savedColumnWidthsRef = useRef<Record<string, number>>({});

  const tableLoading =
    isLoading || isPlaceholderData || (isFetching && showLoader);

  const [
    columnVisibilityModel,
    setColumnVisibilityModel,
    resetColumnVisibility,
  ] = useColumnVisibility({
    storageKey: 'talent-matching-column-visibility',
    defaults: DEFAULT_COLUMN_VISIBILITY_MODEL,
  });

  const [talentMatchingActive, setTalentMatchingActive] = useLocalStorage(
    'talent-matching-active',
    canViewTalentMatchingAiMatchScore
  );

  useEffectOnce(() => {
    if (canViewTalentMatchingAiMatchScore !== talentMatchingActive) {
      resetColumnVisibility();
    }
    setTalentMatchingActive(canViewTalentMatchingAiMatchScore);
  });

  return (
    <>
      <BackgroundRankingBanner onRefresh={onRefreshAfterRanking} />
      <NoOverflowFullWidth>
        <AppReviewTable
          key={mountKey}
          rows={rows}
          buffer={buffer}
          totalRows={pagerTotalRows}
          loading={tableLoading}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          onRowSelected={onRowSelected}
          selectionsHelper={selectionsHelper}
          columnVisibilityModel={columnVisibilityModel}
          setColumnVisibilityModel={setColumnVisibilityModel}
          defaultColumnVisibilityModel={DEFAULT_COLUMN_VISIBILITY_MODEL}
          preSelectedRow={preSelectedRow}
          setPreSelectedRow={setPreSelectedRow}
          savedColumnWidthsRef={savedColumnWidthsRef}
          unmatchedFilterValues={unmatchedFilterValues}
        />
      </NoOverflowFullWidth>
    </>
  );
};

export default AppReviewTableWrapper;
